import { ArrowRight, KeyRound, Mail } from '../components/icons';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Input from '../components/Input';
import ThemeToggle from '../components/ThemeToggle';
import { authService } from '../services/authService';
import { useAuth } from '../store/authContext';
import { useToast } from '../store/toastContext';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EyeIcon = ({ off = false }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
    <circle cx="12" cy="12" r="3" />
    {off ? <path d="M4 4l16 16" /> : null}
  </svg>
);

const toPtError = (err: unknown) => {
  const msg = err instanceof Error ? err.message : 'Erro inesperado.';
  const lower = msg.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('network')) return 'Falha de conexão com o servidor.';
  if (lower.includes('invalid') && lower.includes('password')) return 'Email ou senha inválidos.';
  if (lower.includes('email or password')) return 'Email ou senha inválidos.';
  return msg;
};

export default function LoginPage() {
  const { login, loading } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('admin@bimprospect.com');
  const [password, setPassword] = useState('12345678');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as { from?: string } | null)?.from || '/dashboard';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!emailRegex.test(email)) {
      const msg = 'Informe um email válido.';
      setError(msg);
      pushToast({ type: 'error', title: msg });
      return;
    }
    if (password.length < 8) {
      const msg = 'Senha deve ter no mínimo 8 caracteres.';
      setError(msg);
      pushToast({ type: 'error', title: msg });
      return;
    }

    try {
      await login(email, password);
      const session = authService.getSession();
      pushToast({
        type: 'success',
        title: `Bem-vindo, ${session?.user.name || 'usuário'}`,
        description: 'Login realizado com sucesso.'
      });
      navigate(from, { replace: true });
    } catch (err) {
      const msg = toPtError(err);
      setError(msg);
      pushToast({ type: 'error', title: msg });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-bgLight via-slate-100 to-bgLight px-4 py-8 dark:from-bgDark dark:via-[#081325] dark:to-bgDark">
      <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-primary/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-cta/25 blur-3xl" />

      <div className="relative mx-auto grid min-h-[92vh] max-w-6xl items-center gap-8 lg:grid-cols-2">
        <div className="hidden lg:block">
          <span className="inline-flex rounded-full border border-borderLight bg-white/80 px-3 py-1 text-xs font-semibold dark:border-borderDark dark:bg-cardDark/80">
            BIM PROSPECT
          </span>
          <h1 className="mt-4 text-5xl font-black leading-tight">Prospecção premium para equipes que vendem com consistência.</h1>
          <p className="mt-4 max-w-lg text-slate-600 dark:text-slate-300">UX moderna, automação preparada para n8n e visão completa do que move receita.</p>
        </div>

        <div className="rounded-3xl border border-borderLight bg-white/90 p-6 shadow-2xl backdrop-blur md:p-8 dark:border-borderDark dark:bg-cardDark/75">
          <div className="mb-5 flex items-center justify-between">
            <Link to="/" className="text-sm font-semibold text-primary dark:text-cta">
              Voltar
            </Link>
            <ThemeToggle />
          </div>

          <h2 className="text-2xl font-bold">Entrar</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Acesse seu workspace comercial.</p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} iconLeft={<Mail className="h-4 w-4" />} />
            <Input
              label="Senha"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              iconLeft={<KeyRound className="h-4 w-4" />}
            />

            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300"
            >
              <EyeIcon off={show} />
              {show ? 'Ocultar senha' : 'Mostrar senha'}
            </button>

            {error ? <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-500">{error}</p> : null}

            <Button fullWidth variant="primary" disabled={loading} iconLeft={<ArrowRight className="h-4 w-4" />}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="font-medium text-primary dark:text-cta">
              Esqueci minha senha
            </Link>
            <Link to="/register" className="font-medium text-primary dark:text-cta">
              Criar conta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
